import { set, get, keys, del, createStore } from 'idb-keyval';
import { gzip, ungzip } from 'pako'; // ADDED: for compression
// ADDED: memory tier
import { getFromMemory, setToMemory, delFromMemory } from './memoryCache';
import { clearChangedSinceForKeys } from './changedSinceStore';
import { SERVER_VERSION } from '../enviromentValues';

type KVStore = ReturnType<typeof createStore> | undefined;

// Global active store for most app data. Auth layer controls it.
let currentStore: KVStore = undefined;
let currentUserScope: string | null = null;

export function getCacheUserScope(): string | null {
  return currentUserScope;
}

// -----------------------------
// Constants & types
// -----------------------------

const SERVER_VERSION_CACHE_KEY = '__server_version__';

export const PROTECTED_KEYS = ['/api/GetUserPreferences', 'TOKEN', SERVER_VERSION_CACHE_KEY] as const;
const MANIFEST_KEY = '__idb_manifest__';
// ADDED: lightweight base-path → latest-fullKey index
const INDEX_KEY = '__by_basepath__';
type Manifest = Record<string, number>;
type BaseIndex = Record<string, string>;
let IDB_WRITE_BLOCKED_UNTIL_RELOAD = false;

// Server-version check singleton
let serverVersionCheckPromise: Promise<void> | null = null;
let serverVersionInitializedForScope: string | null = null;

// NEW: names used for cross-user purge
const COMMON_DB_NAME = 'keyval';
const APP_DB_PREFIX = 'app-cache-';

// MUST stay in sync with useGetAllPaginatedItems localStorage meta prefix
const CACHE_SIZE_META_PREFIX = 'cache_size_';

export function setCacheUser(username: string | null) {
  currentStore = username ? createStore(`app-cache-${hashUsername(username)}`, 'kv') : undefined;
  currentUserScope = username ? hashUsername(username) : null; // scoped, not raw PII

  if (!username) {
    serverVersionCheckPromise = null;
    serverVersionInitializedForScope = null;
    return;
  }

  void initializeCacheServerVersion();
}

export async function initializeCacheServerVersion(): Promise<void> {
  const store = getStore();
  const scope = currentUserScope;

  if (!store || !scope) return;

  if (serverVersionInitializedForScope === scope) return;

  if (serverVersionCheckPromise) {
    return serverVersionCheckPromise;
  }

  serverVersionCheckPromise = (async () => {
    try {
      const cachedVersion = await get<number>(SERVER_VERSION_CACHE_KEY, store);

      const shouldClear =
        cachedVersion === undefined ||
        cachedVersion === null ||
        SERVER_VERSION > cachedVersion;

      if (shouldClear) {
        await clearDBExceptKeys(PROTECTED_KEYS as unknown as string[]);
        await set(SERVER_VERSION_CACHE_KEY, SERVER_VERSION, store);
      } else if (SERVER_VERSION !== cachedVersion) {
        await set(SERVER_VERSION_CACHE_KEY, SERVER_VERSION, store);
      }

      serverVersionInitializedForScope = scope;
    } catch (err) {
      console.error('Error checking server version cache:', err);
    } finally {
      serverVersionCheckPromise = null;
    }
  })();

  return serverVersionCheckPromise;
}

export function getUserStore(username: string | null): KVStore {
  return username ? createStore(`app-cache-${hashUsername(username)}`, 'kv') : undefined;
}

function getStore(): KVStore {
  return currentStore;
}

// Simple, synchronous hash to avoid raw PII in DB names.
function hashUsername(input: string): string {
  let h = 5381;
  for (let i = 0; i < input.length; i++) {
    h = ((h << 5) + h) ^ input.charCodeAt(i);
  }
  return (h >>> 0).toString(16);
}

// -----------------------------
// ADDED: codecs (minify + gzip) for large values
// -----------------------------

type EncodedPayload =
  | { __v: 1; c: 'none'; d: any }
  | { __v: 1; c: 'gzip-json'; d: Uint8Array };

function encodeForIDB(value: any, useCompression: boolean, minify: boolean): EncodedPayload {
  if (!useCompression) return { __v: 1, c: 'none', d: value };
  const json = JSON.stringify(value, null, minify ? 0 : 2);
  const gz = gzip(json);
  if (typeof console !== 'undefined') {
    const rawKB = (json.length / 1024).toFixed(1);
    const gzKB = (gz.length / 1024).toFixed(1);
    // console.log(
    //   `[IDB compress] key size ${rawKB} KB → ${gzKB} KB (${(
    //     (gz.length / json.length) * 100
    //   ).toFixed(1)}%)`
    // );
  }
  return { __v: 1, c: 'gzip-json', d: gz };
}

function decodeFromIDB(payload: any): any {
  if (!payload || payload.__v !== 1) return payload;
  if (payload.c === 'none') return payload.d;
  if (payload.c === 'gzip-json') {
    const text = new TextDecoder().decode(ungzip(payload.d));
    return JSON.parse(text);
  }
  return payload;
}

/** Heuristic: compress only if JSON would exceed ~100 kB */
function shouldCompressHeuristic(val: any): boolean {
  if (val == null) return false;

  // Large arrays: decide based on item count only
  if (Array.isArray(val)) {
    // tune this threshold to taste
    return val.length > 2_000;
  }

  // Strings: simple length check
  if (typeof val === 'string') {
    return val.length > 100_000;
  }

  // Objects: be conservative – don't compress unless explicitly allowlisted
  return false;
}

/** Optional allowlist for guaranteed compression (paths without query) */
const COMPRESS_KEYS = new Set<string>([
  // Add high-volume endpoints here if you want to force compression
  // '/api/Products/GetAll',
]);

function wantsCompression(key: string, val: any): boolean {
  const path = extractApiPath(key);
  if (COMPRESS_KEYS.has(path)) return true;
  return shouldCompressHeuristic(val);
}

const runIdle = (cb: () => void) => {
  if ('requestIdleCallback' in window) {
    (window as any).requestIdleCallback(cb, { timeout: 2000 });
  } else {
    setTimeout(cb, 50);
  }
};

// -----------------------------
// Store-agnostic manifest helpers (use given store)
// -----------------------------

async function loadManifestFrom(store: KVStore): Promise<Manifest> {
  try {
    const m = await get<Manifest>(MANIFEST_KEY, store);
    return (m && typeof m === 'object') ? m : {};
  } catch {
    return {};
  }
}
async function saveManifestTo(store: KVStore, m: Manifest): Promise<void> {
  try { await set(MANIFEST_KEY, m, store); } catch { }
}
async function touchManifestIn(store: KVStore, key: string): Promise<void> {
  const m = await loadManifestFrom(store);
  m[key] = Date.now();
  await saveManifestTo(store, m);
}
async function removeFromManifestIn(store: KVStore, keysToRemove: string[]): Promise<void> {
  if (!keysToRemove?.length) return;
  const m = await loadManifestFrom(store);
  let changed = false;
  for (const k of keysToRemove) {
    if (k in m) { delete m[k]; changed = true; }
  }
  if (changed) await saveManifestTo(store, m);
}

// -----------------------------
// ADDED: base-path index helpers
// -----------------------------
async function loadIndexFrom(store: KVStore): Promise<BaseIndex> {
  try {
    const idx = await get<BaseIndex>(INDEX_KEY, store);
    return (idx && typeof idx === 'object') ? idx : {};
  } catch {
    return {};
  }
}
async function saveIndexTo(store: KVStore, idx: BaseIndex): Promise<void> {
  try { await set(INDEX_KEY, idx, store); } catch { }
}
async function updateIndexFor(store: KVStore, fullKey: string): Promise<void> {
  const base = extractApiPathNoPageParams(extractApiPath(fullKey));
  const idx = await loadIndexFrom(store);
  if (idx[base] !== fullKey) {
    idx[base] = fullKey;
    await saveIndexTo(store, idx);
  }
}
async function removeFromIndex(store: KVStore, keysToRemove: string[]): Promise<void> {
  if (!keysToRemove?.length) return;
  const idx = await loadIndexFrom(store);
  let changed = false;
  const valSet = new Set(keysToRemove);
  for (const [bp, fk] of Object.entries(idx)) {
    if (valSet.has(fk)) { delete idx[bp]; changed = true; }
  }
  if (changed) await saveIndexTo(store, idx);
}

// -----------------------------
// ADDED: localStorage meta cleanup for deleted keys
// -----------------------------
function clearCacheSizeMetaForKeys(keysToRemove: string[]): void {
  if (typeof window === 'undefined') return;
  if (!keysToRemove?.length) return;

  try {
    const seen = new Set<string>();
    for (const k of keysToRemove) {
      const path = extractApiPath(String(k));
      if (!path || seen.has(path)) continue;
      window.localStorage.removeItem(CACHE_SIZE_META_PREFIX + path);
      seen.add(path);
      clearChangedSinceForKeys([k]);
    }
  } catch {
    // ignore localStorage issues
  }
}

// -----------------------------
// Common-store (origin-wide) helpers — always use default store
// -----------------------------

export async function setToCommonDB<T>(key: string, val: T): Promise<void> {
  if (typeof val === 'undefined') return;
  try {
    await set(key, val, undefined);       // default global store
    await touchManifestIn(undefined, key); // manifest in common store
  } catch (err) {
    // keep simple; token path shouldn’t do quota eviction
    console.error('Error setting value to common IndexedDB:', err);
  }
}

export async function getFromCommonDB<T>(key: string): Promise<T | null> {
  try {
    const v = await get<T>(key, undefined); // default global store
    return v ?? null;
  } catch (err) {
    console.error('Error getting value from common IndexedDB:', err);
    return null;
  }
}

export async function delFromCommonDB(key: string): Promise<void> {
  try {
    await del(key, undefined);
    await removeFromManifestIn(undefined, [key]);
  } catch (err) {
    console.error('Error deleting value from common IndexedDB:', err);
  }
}

// -----------------------------
// Public helpers (current store)
// -----------------------------

export async function evictSomeKeys(
  exclude: string[] = PROTECTED_KEYS as unknown as string[],
  targetDeletes = 10,
  currentKey?: string
): Promise<number> {
  const store = getStore();
  const excludeSet = new Set<string>([...exclude, MANIFEST_KEY, INDEX_KEY]);
  if (currentKey) excludeSet.add(currentKey);

  const manifest = await loadManifestFrom(store);
  let candidates: string[] = Object.keys(manifest).filter(k => !excludeSet.has(k));

  if (candidates.length === 0) {
    try {
      const all = await keys(store);
      candidates = all.map(k => String(k)).filter(s => !excludeSet.has(s));
    } catch {
      candidates = [];
    }
  } else {
    candidates.sort((a, b) => (manifest[a] ?? 0) - (manifest[b] ?? 0));
  }

  let deleted = 0;
  const actuallyDeleted: string[] = [];

  for (const k of candidates) {
    try {
      await del(k, store);
      actuallyDeleted.push(k);
      deleted++;
      // ADDED: keep memory tier and index in sync
      delFromMemory(k);
      if (deleted >= targetDeletes) break;
    } catch { }
  }

  if (actuallyDeleted.length) {
    await removeFromManifestIn(store, actuallyDeleted);
    await removeFromIndex(store, actuallyDeleted); // ADDED
    clearCacheSizeMetaForKeys(actuallyDeleted);    // ADDED: clear localStorage meta
  }

  return deleted;
}

// -----------------------------
// NEW: cross-user purge utilities (minimal, self-contained)
// -----------------------------

function getCurrentDbName(): string | undefined {
  try {
    return (getStore() as any)?.dbName as string | undefined;
  } catch {
    return undefined;
  }
}

function deleteDatabaseByName(name: string): Promise<void> {
  return new Promise((resolve) => {
    const req = indexedDB.deleteDatabase(name);
    req.onsuccess = () => resolve();
    req.onerror = () => resolve();
    req.onblocked = () => resolve();
  });
}

/**
 * Deletes every app-cache DB not belonging to the current user,
 * and never deletes the common 'keyval' DB.
 * If indexedDB.databases() is unavailable, this is a no-op.
 */
async function purgeOtherUserStores(): Promise<number> {
  const databasesFn = (indexedDB as any)?.databases;
  if (typeof databasesFn !== 'function') return 0;

  let dbs: Array<{ name?: string | null }> = [];
  try {
    dbs = await databasesFn();
  } catch {
    return 0;
  }

  const currentName = getCurrentDbName();

  const toDelete = dbs
    .map(d => d?.name || '')
    .filter(Boolean)
    .filter(name =>
      name !== COMMON_DB_NAME &&
      name !== currentName &&
      name.startsWith(APP_DB_PREFIX)
    );

  let deleted = 0;
  for (const name of toDelete) {
    try {
      await deleteDatabaseByName(name);
      deleted++;
    } catch {
      // best-effort
    }
  }
  return deleted;
}

// -----------------------------
// Core cache API (current store)
// -----------------------------

export const setToDB = async <T>(key: string, val: T): Promise<void> => {
  if (typeof val === 'undefined') return;
  if (IDB_WRITE_BLOCKED_UNTIL_RELOAD) return;
  const store = getStore();

  // ADDED: populate memory tier immediately (uncompressed)
  try { setToMemory(key, val); } catch { }
  runIdle(async () => {
    try {
      // ADDED: encode (minify+gzip based on heuristic)
      const encoded = encodeForIDB(val, wantsCompression(key, val), /*minify*/ true);
      await set(key, encoded, store);
      await touchManifestIn(store, key);
      // ADDED: keep base-path index fresh
      await updateIndexFor(store, key);
    } catch (err: any) {
      const isQuota =
        err?.name === 'QuotaExceededError' ||
        err?.code === 22 ||
        /quota/i.test(String(err?.message || ''));

      if (isQuota) {
        try {
          // NEW: purge other users' app-cache DBs first, then retry
          await purgeOtherUserStores();
          // RETRY with same encoded payload
          const encoded = encodeForIDB(val, wantsCompression(key, val), /*minify*/ true);
          await set(key, encoded, store);
          await touchManifestIn(store, key);
          await updateIndexFor(store, key); // ADDED
          return;
        } catch {
          // fall through to in-store eviction
        }

        try {
          await evictSomeKeys(PROTECTED_KEYS as unknown as string[], 10, key);
          // RETRY with same encoded payload
          const encoded = encodeForIDB(val, wantsCompression(key, val), /*minify*/ true);
          await set(key, encoded, store);
          await touchManifestIn(store, key);
          await updateIndexFor(store, key); // ADDED
          return;
        } catch (err2) {
          console.error('IDB quota after eviction, disabling further writes this session:', err2);
          IDB_WRITE_BLOCKED_UNTIL_RELOAD = true;
          return;
        }
      }

      console.error('Error setting value to IndexedDB:', err);
    }
  });
};

export const getFromDB = async <T>(key: string): Promise<T | null> => {
  if (!key) {
    console.error('Invalid key provided:', key);
    return null;
  }

  // ADDED: memory-fast path for exact key
  try {
    const mem = getFromMemory<T>(key);
    if (mem !== null) return mem;
  } catch { }

  try {
    const cachedValue = await get<any>(key, getStore());
    if (cachedValue === undefined || cachedValue === null) return null;
    // ADDED: decode if encoded
    const decoded = decodeFromIDB(cachedValue) as T;
    // ADDED: promote to memory
    try { setToMemory(key, decoded); } catch { }
    return decoded;
  } catch (err) {
    console.error('Error getting value from IndexedDB:', err);
    return null;
  }
};

// -----------------------------
// Store-specific APIs (do not depend on global store)
// -----------------------------

export const setToDBInStore = async <T>(store: KVStore, key: string, val: T): Promise<void> => {
  if (typeof val === 'undefined') return;
  try {
    // ADDED: encode (minify+gzip based on heuristic)
    const encoded = encodeForIDB(val, wantsCompression(key, val), /*minify*/ true);
    await set(key, encoded, store);
    await touchManifestIn(store, key);
    // ADDED: keep base-path index fresh (if using current store)
    if (store === getStore()) await updateIndexFor(store, key);
  } catch (err) {
    console.error('Error setting value to IndexedDB (store-specific):', err);
  }
};

export const getFromDBInStore = async <T>(store: KVStore, key: string): Promise<T | null> => {
  try {
    const v = await get<any>(key, store);
    if (v === undefined || v === null) return null;
    // ADDED: decode if encoded
    return decodeFromIDB(v) as T;
  } catch (err) {
    console.error('Error getting value from IndexedDB (store-specific):', err);
    return null;
  }
};

// -----------------------------
// Helpers used by callers
// -----------------------------

export const extractApiPath = (url: string) => {
  const apiIndex = url.indexOf('/api/');
  return apiIndex !== -1 ? url.substring(apiIndex) : url;
};

export const getFromDBandFile = async <T>(key: string, useFile: boolean | string = false): Promise<T | null> => {
  if (!key) {
    console.error('Invalid key provided:', key);
    return null;
  }
  try {
    const apiPathKey = extractApiPath(key);
    const base = extractApiPathNoPageParams(apiPathKey);

    // ADDED: consult index for latest fullKey by base-path
    let fullKey = '';
    try {
      const idx = await get<BaseIndex>(INDEX_KEY, getStore());
      fullKey = (idx && idx[base]) || '';
    } catch { }

    // ADDED: memory fast-path by indexed fullKey
    if (fullKey) {
      const memHit = getFromMemory<T>(fullKey);
      if (memHit !== null) return memHit;

      const fromExact = await getFromDB<T>(fullKey);
      if (fromExact !== null) return fromExact;
    }

    // FALLBACK: scan (kept for safety with tiny overhead at ~100 keys)
    const allKeys = await keys(getStore());
    let matchedButEmpty = false;

    for (const cacheKey of allKeys) {
      const cacheApiPath = extractApiPath(String(cacheKey));
      if (apiPathKey === cacheApiPath) {
        // ADDED: memory fast-path for this exact key
        const mem = getFromMemory<T>(String(cacheKey));
        if (mem !== null) return mem;

        const raw = await get<any>(String(cacheKey), getStore());
        if (raw !== undefined && raw !== null) {
          // 🔧 decode encoded payloads before returning
          const decoded = decodeFromIDB(raw) as T;
          // ADDED: promote to memory and refresh index
          try {
            setToMemory(String(cacheKey), decoded);
            await updateIndexFor(getStore(), String(cacheKey));
          } catch { }
          return decoded as T;
        }
        matchedButEmpty = true;
      }
    }

    if (!useFile || useFile === 'false') {
      return null;
    }

    const response = await fetch('/cache.json');
    if (!response.ok) {
      throw new Error('Failed to fetch cache.json');
    }
    const cache = await response.json();

    for (const cacheKey in cache) {
      if (Object.prototype.hasOwnProperty.call(cache, cacheKey)) {
        const cacheApiPath = extractApiPath(cacheKey);
        if (apiPathKey === cacheApiPath) {
          const valueFromCacheFile = cache[cacheKey];
          // Persist (this will encode if needed)
          await setToDB(cacheKey, valueFromCacheFile);
          // Return the plain value to callers
          return valueFromCacheFile as T;
        }
      }
    }

    return null;
  } catch (err) {
    console.error('Error getting value:', err);
    return null;
  }
};

// -----------------------------
// Clear helpers (current store)
// -----------------------------

export const clearDBExceptKey = async (keyToKeep: string): Promise<void> => {
  if (!keyToKeep) {
    console.error('Invalid key to keep:', keyToKeep);
    return;
  }

  try {
    const store = getStore();
    const allKeys = await keys(store);
    const deleted: string[] = [];

    for (const key of allKeys) {
      const s = String(key);
      if (s !== keyToKeep && s !== MANIFEST_KEY && s !== INDEX_KEY) {
        await del(key, store);
        deleted.push(s);
        // ADDED: keep memory in sync
        delFromMemory(s);
      }
    }

    if (deleted.length) {
      await removeFromManifestIn(store, deleted);
      await removeFromIndex(store, deleted);      // ADDED
      clearCacheSizeMetaForKeys(deleted);         // ADDED
    }

    // console.log(`Cleared all keys except: ${keyToKeep}`);
  } catch (err) {
    //  console.error('Error clearing IndexedDB keys:', err);
  }
};

export const clearDBExceptKeys = async (keysToKeep: string[]): Promise<void> => {
  if (!Array.isArray(keysToKeep) || keysToKeep.length === 0) {
    console.error('Invalid keys to keep:', keysToKeep);
    return;
  }

  try {
    const store = getStore();
    const keepSet = new Set<string>([...keysToKeep, MANIFEST_KEY, INDEX_KEY]);
    const allKeys = await keys(store);
    const deleted: string[] = [];

    for (const key of allKeys) {
      const keyStr = String(key);
      if (!keepSet.has(keyStr)) {
        await del(key, store);
        deleted.push(keyStr);
        // ADDED: keep memory in sync
        delFromMemory(keyStr);
      }
    }

    if (deleted.length) {
      await removeFromManifestIn(store, deleted);
      await removeFromIndex(store, deleted);      // ADDED
      clearCacheSizeMetaForKeys(deleted);         // ADDED
    }

    // console.log(`Cleared all keys except: ${keysToKeep.join(', ')}`);
  } catch (err) {
    console.error('Error clearing IndexedDB keys:', err);
  }
};

export const clearCache = async (): Promise<void> => {
  try {
    await clearDBExceptKeys(PROTECTED_KEYS as unknown as string[]);

    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const eqPos = cookie.indexOf('=');
      const name = eqPos > -1 ? cookie.substring(0, eqPos) : cookie;
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
    }

    // console.log('Cache and cookies cleared successfully');
  } catch (err) {
    console.error('Error clearing cache and cookies:', err);
  }
};

function extractApiPathNoPageParams(fullUrl: string): string {
  const qIndex = fullUrl.indexOf('?');
  if (qIndex === -1) return fullUrl;

  const base = fullUrl.slice(0, qIndex);
  const query = fullUrl.slice(qIndex + 1);
  if (!query) return base;

  const parts = query.split('&').filter(Boolean);

  const filtered = parts.filter((p) => {
    const eq = p.indexOf('=');
    const key = eq === -1 ? p : p.slice(0, eq);
    return key !== 'page';
  });

  if (filtered.length === 0) return base;
  return `${base}?${filtered.join('&')}`;
}

export async function clearCacheForUrl(fullUrl: string): Promise<void> {
  if (!fullUrl) return;
  const baseToMatch = extractApiPathNoPageParams(extractApiPath(fullUrl));

  try {
    const store = getStore();
    const allKeys = await keys(store);
    const deleted: string[] = [];

    for (const k of allKeys) {
      const keyString = String(k);
      if (keyString === MANIFEST_KEY || keyString === INDEX_KEY) continue;

      const baseKey = extractApiPathNoPageParams(extractApiPath(keyString));
      if (baseKey === baseToMatch) {
        await del(k, store);
        deleted.push(keyString);
        // ADDED: memory sync
        delFromMemory(keyString);
      }
    }

    if (deleted.length) {
      await removeFromManifestIn(store, deleted);
      await removeFromIndex(store, deleted);      // ADDED
      clearCacheSizeMetaForKeys(deleted);         // ADDED
    }
  } catch (err) {
    console.error('Error clearing cache:', err);
  }
}